import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, 
  ArrowRight, 
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  Megaphone,
  MessageSquare,
  Lightbulb,
  Truck,
  Users,
  BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { useApp } from '@/context/AppContext';

const dimensionIcons = {
  'Convocatoria': Megaphone,
  'Comunicación': MessageSquare,
  'Metodologías': Lightbulb,
  'Logística': Truck,
  'Aliados externos': Users,
  'Retroalimentación': BarChart3
};

const dimensionColors = {
  'Convocatoria': { bg: 'bg-primary/10', border: 'border-l-primary', text: 'text-primary' },
  'Comunicación': { bg: 'bg-secondary/10', border: 'border-l-secondary', text: 'text-secondary' },
  'Metodologías': { bg: 'bg-accent/10', border: 'border-l-accent', text: 'text-accent' },
  'Logística': { bg: 'bg-success/10', border: 'border-l-success', text: 'text-success' },
  'Aliados externos': { bg: 'bg-chart-5/10', border: 'border-l-chart-5', text: 'text-chart-5' },
  'Retroalimentación': { bg: 'bg-destructive/10', border: 'border-l-destructive', text: 'text-destructive' }
};

export const ResultsPage = () => {
  const { 
    t, 
    language,
    calculateScores, 
    resetDiagnosis,
    isCompleted,
    getAllCommitments
  } = useApp();

  const { dimensionScores, overallScore, dimensionDetails } = useMemo(() => {
    return calculateScores();
  }, [calculateScores]);

  const commitments = getAllCommitments();

  // Prepare radar chart data
  const radarData = useMemo(() => {
    return dimensionDetails.map(dim => ({
      dimension: language === 'en' ? t(`dimensions.${dim.id}`) : dim.id,
      score: dim.score,
      fullMark: 5
    }));
  }, [dimensionDetails, language, t]);

  // Get score display info - Basado en el nuevo Nivel de Inclusión
  // 1.0 - 2.5: Área Crítica / Por Explorar (Rojo)
  // 2.6 - 3.9: En Proceso (Amarillo)
  // 4.0 - 5.0: Fortaleza / Práctica Inclusiva (Verde)
  const getScoreInfo = (score) => {
    if (score >= 4.0) {
      return { 
        label: t('scoreHigh'), 
        color: 'text-success', 
        bgColor: 'bg-success/10',
        icon: TrendingUp 
      };
    }
    if (score >= 2.6) {
      return { 
        label: t('scoreMedium'), 
        color: 'text-warning', 
        bgColor: 'bg-warning/10',
        icon: Minus 
      };
    }
    return { 
      label: t('scoreLow'), 
      color: 'text-destructive', 
      bgColor: 'bg-destructive/10',
      icon: TrendingDown 
    };
  };

  const overallInfo = getScoreInfo(overallScore);

  const handlePrint = () => {
    window.print();
  };

  const handleRestart = () => {
    if (window.confirm(language === 'en' 
      ? 'Are you sure you want to restart? All progress will be lost.' 
      : '¿Estás seguro de que quieres reiniciar? Se perderá todo el progreso.')) {
      resetDiagnosis();
      window.location.href = '/';
    }
  };

  // Redirect if not completed
  if (!isCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="card-brand max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold text-foreground mb-4">
              {language === 'en' ? 'No results yet' : 'Aún no hay resultados'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {language === 'en' 
                ? 'Please complete the questionnaire first to see your results.'
                : 'Por favor completa el cuestionario primero para ver tus resultados.'}
            </p>
            <Button asChild className="btn-brand-primary">
              <a href="/questionnaire">{t('startDiagnosis')}</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-4">
            {t('resultsTitle')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('resultsSubtitle')}
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Overall Score Card */}
          <motion.div variants={itemVariants}>
            <Card className="card-brand overflow-hidden">
              <CardContent className="p-0">
                <div className="grid md:grid-cols-2 gap-0">
                  {/* Score Display */}
                  <div className="p-8 flex flex-col items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
                    <span className="text-sm font-medium text-muted-foreground mb-2">
                      {t('overallScore')}
                    </span>
                    <div className="relative mb-4">
                      <div className="w-32 h-32 rounded-full bg-card border-4 border-secondary flex items-center justify-center shadow-blue-glow">
                        <span className="text-4xl font-extrabold text-foreground">
                          {overallScore.toFixed(1)}
                        </span>
                      </div>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${overallInfo.bgColor}`}>
                      <overallInfo.icon className={`h-4 w-4 ${overallInfo.color}`} />
                      <span className={`font-semibold ${overallInfo.color}`}>
                        {overallInfo.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4 text-center">
                      {language === 'en' 
                        ? 'Higher score = more inclusive practices'
                        : 'Mayor puntaje = prácticas más inclusivas'}
                    </p>
                  </div>

                  {/* Radar Chart */}
                  <div className="p-6 min-h-[300px]">
                    <h3 className="text-lg font-bold text-foreground mb-4 text-center">
                      {t('radarChartTitle')}
                    </h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                        <PolarGrid stroke="hsl(var(--border))" />
                        <PolarAngleAxis 
                          dataKey="dimension" 
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        />
                        <PolarRadiusAxis 
                          angle={30} 
                          domain={[0, 5]} 
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        />
                        <Radar
                          name="Score"
                          dataKey="score"
                          stroke="hsl(var(--secondary))"
                          fill="hsl(var(--secondary))"
                          fillOpacity={0.4}
                          strokeWidth={2}
                        />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '0.75rem',
                            boxShadow: 'var(--shadow-md)'
                          }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Dimension Scores */}
          <motion.div variants={itemVariants}>
            <h2 className="text-2xl font-bold text-foreground mb-6">
              {t('dimensionScores')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dimensionDetails.map((dim) => {
                const Icon = dimensionIcons[dim.id];
                const colors = dimensionColors[dim.id];
                const scoreInfo = getScoreInfo(dim.score);
                
                return (
                  <motion.div
                    key={dim.id}
                    variants={itemVariants}
                    whileHover={{ y: -4 }}
                  >
                    <Card className={`card-brand border-l-4 ${colors.border} h-full`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
                            <Icon className={`h-5 w-5 ${colors.text}`} />
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${scoreInfo.bgColor} ${scoreInfo.color}`}>
                            {scoreInfo.label}
                          </div>
                        </div>
                        <h3 className="font-bold text-foreground mb-1">
                          {language === 'en' ? t(`dimensions.${dim.id}`) : dim.id}
                        </h3>
                        <p className="text-xs text-muted-foreground mb-3">
                          {language === 'en' 
                            ? t(`dimensionDescriptions.${dim.id}`)
                            : dim.description}
                        </p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <motion.div
                              className={`h-full ${colors.bg.replace('/10', '')}`}
                              initial={{ width: 0 }}
                              animate={{ width: `${(dim.score / 5) * 100}%` }}
                              transition={{ duration: 0.8, delay: 0.2 }}
                            />
                          </div>
                          <span className="text-lg font-bold text-foreground">
                            {dim.score.toFixed(1)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Commitments Summary (if any) */}
          {commitments.length > 0 && (
            <motion.div variants={itemVariants}>
              <Card className="card-brand border-2 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-xl text-foreground">
                    {t('myCommitments')} ({commitments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {commitments.slice(0, 5).map((c, index) => (
                      <li key={index} className="flex items-start gap-3 text-sm">
                        <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-primary">{index + 1}</span>
                        </div>
                        <span className="text-muted-foreground">{c.tip}</span>
                      </li>
                    ))}
                    {commitments.length > 5 && (
                      <li className="text-sm text-muted-foreground pl-8">
                        + {commitments.length - 5} {language === 'en' ? 'more' : 'más'}...
                      </li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Actions */}
          <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-center gap-4 pt-4">
            <Button
              onClick={handlePrint}
              variant="outline"
              className="btn-brand-outline no-print"
            >
              <Download className="mr-2 h-4 w-4" />
              {t('saveReport')}
            </Button>
            <Button
              asChild
              className="btn-brand-primary no-print"
            >
              <a href="/action-plan">
                {t('goToActionPlan')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleRestart}
              className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:border-destructive no-print"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('restartDiagnosis')}
            </Button>
          </motion.div>
        </motion.div>

        {/* Print-only content */}
        <div className="hidden print:block mt-8 pt-8 border-t border-border">
          <p className="text-sm text-muted-foreground text-center">
            {t('printGeneratedBy')} - {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
